import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { BottomNav, TopBar } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Chat = ({ user }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (userId) {
      const foundUser = users.find(u => u.user_id === userId);
      if (foundUser) {
        handleSelectUser(foundUser);
      }
    }
  }, [userId, users]);

  useEffect(() => {
    if (selectedUser) {
      const interval = setInterval(() => {
        fetchMessages(selectedUser.user_id);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const response = await api.getChatUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleSelectUser = async (selectedUser) => {
    setSelectedUser(selectedUser);
    await fetchMessages(selectedUser.user_id);
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await api.getMessages(otherUserId);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    setLoading(true);
    try {
      await api.sendMessage({
        receiver_id: selectedUser.user_id,
        content: newMessage,
      });
      setNewMessage('');
      await fetchMessages(selectedUser.user_id);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (selectedUser) {
    return (
      <div className="min-h-screen bg-slate-50 pb-nav flex flex-col">
        {/* Chat Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <Button
            data-testid="back-to-chats"
            onClick={() => {
              setSelectedUser(null);
              navigate('/chat');
            }}
            variant="ghost"
            size="sm"
          >
            <ArrowLeft size={20} />
          </Button>
          <Avatar className="avatar-ring">
            <AvatarFallback className="bg-orange-100 text-orange-600 font-semibold">
              {selectedUser.rep_name?.[0]?.toUpperCase() || selectedUser.email[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold text-slate-900">{selectedUser.rep_name || selectedUser.email}</p>
            <p className="text-xs text-slate-500">{selectedUser.role} • {selectedUser.city}</p>
          </div>
        </div>

        {/* Messages */}
        <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isSent = msg.sender_id === user.user_id;
              return (
                <div key={msg.message_id} className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] ${isSent ? 'chat-bubble-sent' : 'chat-bubble-received'} px-4 py-2`}>
                    <p className="text-sm">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isSent ? 'text-white/70' : 'text-slate-400'}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Input */}
        <div className="bg-white border-t border-slate-200 p-4">
          <div className="flex gap-2">
            <Input
              data-testid="message-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type a message..."
            />
            <Button
              data-testid="send-message-button"
              onClick={handleSendMessage}
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Send size={18} />
            </Button>
          </div>
        </div>

        <BottomNav user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-nav">
      <TopBar title="Messages" subtitle="Chat with your team" user={user} />

      <div className="p-4 md:p-8 space-y-3 max-w-2xl mx-auto">
        {users.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500">No users available to chat</p>
          </Card>
        ) : (
          users.map((chatUser) => (
            <Card
              key={chatUser.user_id}
              data-testid={`chat-user-${chatUser.user_id}`}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                handleSelectUser(chatUser);
                navigate(`/chat/${chatUser.user_id}`);
              }}
            >
              <div className="flex items-center gap-3">
                <Avatar className="avatar-ring">
                  <AvatarFallback className="bg-orange-100 text-orange-600 font-semibold">
                    {chatUser.rep_name?.[0]?.toUpperCase() || chatUser.email[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{chatUser.rep_name || chatUser.email}</p>
                  <p className="text-sm text-slate-500">
                    {chatUser.role} • {chatUser.city} {chatUser.team ? `• ${chatUser.team}` : ''}
                  </p>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <BottomNav user={user} />
    </div>
  );
};

export default Chat;
